<template>
  <div v-loading="searchLoading" class="c-charts" v-bind="$attrs">
    <div v-if="isHeader" class="c-charts__header">
      <div v-if="title" class="title">{{ title }}</div>
      <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
      <Content v-if="header" :content="header" />
    </div>
    <div class="c-charts__body">
      <ECharts ref="chartRef" class="chart" v-bind="chartAttrs" />
    </div>
  </div>
</template>

<script lang="ts">
import {
  _,
  ref,
  watch,
  tpl,
  defineComponent,
  useCurrentAppInstance,
  onMounted,
  onBeforeUnmount,
  computed,
  PAGE_SEARCH_EVENT_KEY
} from '@zto/zpage'

import ECharts from './ECharts'

import { normalizeChartOption } from './utils'

export default defineComponent({
  components: {
    ECharts
  },

  props: {
    api: [String, Object], // 数据接口
    apiParams: [String, Function], // API参数
    immediate: Boolean, // 是否立即查询
    data: Object, // 图表数据
    header: [String, Object], // 头
    title: String, // 标题
    subtitle: String, // 子标题
    option: [Object, Function], // 图表选项
    chartAttrs: Object
  },

  setup(props) {
    const app = useCurrentAppInstance()

    const chartRef = ref()

    // 数据加载
    const searchLoading = ref(false)

    const chartOption = ref()
    const chartData = ref()

    const isHeader = computed(() => {
      return props.title || props.subtitle || props.header
    })

    watch(
      () => [props.option, props.data],
      () => {
        loadChartOption()
      }
    )

    onMounted(() => {
      if (props.immediate) {
        search()
      }

      app.ons(PAGE_SEARCH_EVENT_KEY, search)
    })

    onBeforeUnmount(() => {
      app.offs(PAGE_SEARCH_EVENT_KEY, search)
    })

    /** 查询数据 */
    async function search() {
      if (!props.api) return

      const res: any = await doSearch()

      chartData.value = res.data || {}

      await loadChartOption()
    }

    /**
     * 加载图标数据
     */
    async function loadChartOption() {
      if (!props.api) {
        chartData.value = props.data || {}
      }

      let _chartOption: any = {}

      if (typeof props.option === 'function') {
        const ctx = app.useContext(chartData.value)

        _chartOption = await Promise.resolve().then(() => {
          return (props.option as Function)(ctx)
        })
      } else {
        _chartOption = _.deepClone(props.option)
      }

      chartOption.value = normalizeChartOption(_chartOption, chartData.value)

      if (chartRef.value) {
        chartRef.value.setOption(chartOption.value, {
          notMerge: true,
          lazyUpdate: true,
          slient: true
        })
      }
    }

    /** 执行查询请求 */
    async function doSearch() {
      if (!props.api) return Promise.resolve()

      const context = app.useContext()

      let params: any = undefined

      const pageSearchData = app.getPageSearchData()
      if (pageSearchData) params = tpl.deepFilter(pageSearchData, context)

      if (props.apiParams) {
        const apiParams = tpl.deepFilter(props.apiParams, context)
        params = { ...params, ...apiParams }
      }

      searchLoading.value = true

      const res = await app.request({ action: props.api, params }).finally(() => {
        searchLoading.value = false
      })

      return res
    }

    return {
      chartRef,
      searchLoading,
      isHeader,
      chartOption,
      search
    }
  }
})
</script>

<style lang="scss" scoped>
.c-charts {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__header {
    .title {
      font-weight: bold;
      font-size: 14px;
    }

    .subtitle {
      opacity: 0.6;
    }
  }

  &__body {
    flex: 1;
  }
}
</style>
